import {
  AlertTriangle,
  Check,
  Database,
  Flag,
  GitMerge,
  Lock,
  RefreshCw,
  RotateCcw,
  Undo2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import { KpiTile } from "@/components/cinch/KpiTile";
import { IconButton } from "@/components/cinch/IconButton";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  useChurn,
  usePullChurn,
  useReviewChurnAccount,
  useSettleChurn,
  type ChurnAccount,
  type ChurnDisposition,
  type ChurnOutcome,
  type ReviewStatus,
  type SettledChurn,
} from "../api";
import { fmtCur, fmtCur2, fmtDate, relativeAgo } from "../../renewals/format";

const DISPOSITION_LABEL: Record<ChurnDisposition, string> = {
  confirmed: "Date on record",
  missing_notice: "No notice recorded",
  merged: "Merged away",
  netted_to_zero: "Netted to zero",
};

/** Plain-English flag copy — each of these is a specific thing to go fix. */
const FLAG_LABEL: Record<string, { label: string; help: string }> = {
  billed_short: {
    label: "Billed short",
    help: "They left AFTER the last month we billed — a month we were entitled to and never invoiced.",
  },
  no_billing_record: {
    label: "No billing record",
    help: "Neither Contract Canceled At nor Stop Billing At is set on any contract.",
  },
  sources_disagree: {
    label: "Sources disagree",
    help: "Two systems record end dates more than a month apart.",
  },
  no_cinch_account: {
    label: "No Cinch account",
    help: "A QuickBooks customer with no matching Cinch account, so no contracts to close.",
  },
  no_contracts: {
    label: "No contracts",
    help: "The account exists but carries no non-PO contracts.",
  },
};

/** Where a suggested date came from. */
const SOURCE_LABEL: Record<string, string> = {
  billing: "billing contract",
  hubspot: "HubSpot",
  company: "company record",
  last_billed: "last billed month",
  manual: "set by hand",
};

/**
 * R-04 worklist: accounts that were billing and have gone to $0.
 *
 * Churn is detected from billing behaviour rather than a status field, because
 * the status fields aren't maintained — of the 21 accounts that stopped billing
 * into the July 2026 close, not one carried a billing-side cancel date. So the
 * step shows what each source believes, and the close writes the answer, which
 * is durable and feeds the cash forecast.
 */
export function DirectChurnPanel({ period }: { period: string }) {
  const { data, isLoading, error } = useChurn(period);
  const pull = usePullChurn(period);
  const settleChurn = useSettleChurn(period);
  const [tab, setTab] = useState<ChurnDisposition | "all" | "settled">("all");
  const [settleTarget, setSettleTarget] = useState<ChurnAccount | null>(null);
  const [flagTarget, setFlagTarget] = useState<ChurnAccount | null>(null);

  const accounts = useMemo(() => data?.accounts ?? [], [data]);
  const settled = useMemo(() => data?.settled ?? [], [data]);
  const rows = useMemo(
    () =>
      tab === "all" || tab === "settled"
        ? accounts
        : accounts.filter((a) => a.disposition === tab),
    [accounts, tab],
  );

  const canAct = (data?.can_act ?? false) && !(data?.locked ?? false);
  const pulled = !!data?.pulled_at;
  const s = data?.summary;

  function doPull() {
    pull.mutate(undefined, {
      onSuccess: (p) =>
        toast.success(
          `Pulled churn — ${p.summary?.churned_count ?? 0} accounts stopped billing (${fmtCur(p.summary?.churned_mrr ?? 0)} MRR)`,
        ),
      onError: (e) => toast.error("Pull failed", { description: (e as Error).message }),
    });
  }

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  if (!isLoading && !pulled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed bg-muted/30 px-4 py-10 text-center">
            <Database className="h-8 w-8 text-muted-foreground" />
            <p className="max-w-md text-sm text-muted-foreground">
              Churn hasn't been pulled for {monthLabel(period)}. Pulling finds
              every account that was billing and has gone to $0, and shows what
              each system believes about when they left — the billing contract,
              HubSpot, and the company record.
            </p>
            {canAct ? (
              <Button onClick={doPull} disabled={pull.isPending}>
                <RefreshCw
                  className={cn("mr-1.5 h-4 w-4", pull.isPending && "animate-spin")}
                />
                {pull.isPending ? "Pulling…" : "Pull from Snowflake"}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                An R-04 owner (CS) runs the pull.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const columns: DataTableColumn<ChurnAccount>[] = [
    {
      key: "account",
      header: "Account",
      className: "max-w-[250px]",
      render: (a) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-semibold">
            {a.cinch_account_name ?? a.qb_customer_name ?? `QB ${a.qb_id}`}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {a.cinch_account_name && a.qb_customer_name !== a.cinch_account_name
              ? `${a.qb_customer_name} · `
              : ""}
            QB {a.qb_id}
          </span>
        </div>
      ),
    },
    {
      key: "disposition",
      header: "What it is",
      render: (a) => (
        <div className="flex flex-col items-start gap-1">
          <Badge
            variant={
              a.disposition === "missing_notice"
                ? "warning"
                : a.disposition === "confirmed"
                  ? "danger"
                  : "neutral"
            }
          >
            {a.disposition === "merged" && <GitMerge className="mr-1 h-3 w-3" />}
            {DISPOSITION_LABEL[a.disposition]}
          </Badge>
          {a.flags.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {a.flags
                .filter((f) => f !== "no_billing_record")
                .map((f) => (
                  <span
                    key={f}
                    title={FLAG_LABEL[f]?.help ?? f}
                    className="inline-flex items-center gap-0.5 rounded bg-warning-light px-1 py-px text-[10px] font-medium text-warning"
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {FLAG_LABEL[f]?.label ?? f}
                  </span>
                ))}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "last_billed_month",
      header: "Last billed",
      render: (a) => (
        <div className="flex flex-col">
          <span className="text-sm">{monthLabel(a.last_billed_month?.slice(0, 7))}</span>
          <span className="text-xs text-muted-foreground">
            {a.months_at_zero === 0
              ? "netted to $0"
              : `${a.months_at_zero} mo at $0`}
          </span>
        </div>
      ),
    },
    {
      key: "mrr_prior",
      header: "MRR lost",
      numeric: true,
      render: (a) => (
        <span
          className={cn(
            "font-semibold",
            a.disposition === "merged" && "text-muted-foreground line-through",
          )}
          title={
            a.disposition === "merged"
              ? "Merged into another account — this revenue moved, it didn't leave"
              : undefined
          }
        >
          {fmtCur2(a.mrr_prior)}
        </span>
      ),
    },
    {
      key: "sources",
      header: "What each source says",
      className: "max-w-[220px]",
      render: (a) => <SourceCell account={a} />,
    },
    {
      key: "settled",
      header: "Settled",
      render: (a) =>
        a.settled ? (
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {a.settled.outcome === "churned"
                ? fmtDate(a.settled.end_date)
                : a.settled.outcome === "merged"
                  ? "merged"
                  : "not churn"}
            </span>
            <span className="text-xs text-muted-foreground">
              by {a.settled.decided_by_name ?? "—"}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "review",
      header: "Review",
      align: "right",
      className: "w-56",
      render: (a) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          <StatusBadge value={a.review_status} />
          {canAct && (
            <>
              <IconButton
                title="Settle the dates — the durable answer the forecast reads"
                active={!!a.settled}
                activeClassName="text-success"
                hoverClassName="hover:bg-success-light hover:text-success"
                onClick={() => setSettleTarget(a)}
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title="Flag — needs CS to backfill (add notes)"
                active={a.review_status === "flagged"}
                activeClassName="text-warning"
                hoverClassName="hover:bg-warning-light hover:text-warning"
                onClick={() => setFlagTarget(a)}
              >
                <Flag className="h-3.5 w-3.5" />
              </IconButton>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label="Churned"
          value={fmtCur2(s?.churned_mrr ?? 0)}
          sub={`${s?.churned_count ?? 0} accounts · ${fmtCur(s?.churned_arr ?? 0)} ARR`}
          tone={(s?.churned_mrr ?? 0) > 0 ? "danger" : "positive"}
        />
        <KpiTile
          label="No notice recorded"
          value={String(s?.missing_notice_count ?? 0)}
          sub={`${fmtCur(s?.missing_notice_mrr ?? 0)} MRR unexplained`}
          tone={(s?.missing_notice_count ?? 0) > 0 ? "warning" : "positive"}
          onClick={() =>
            setTab(tab === "missing_notice" ? "all" : "missing_notice")
          }
          selected={tab === "missing_notice"}
        />
        <KpiTile
          label="Billed short"
          value={String(s?.billed_short_count ?? 0)}
          sub="left after our last invoice"
          tone={(s?.billed_short_count ?? 0) > 0 ? "warning" : "positive"}
        />
        <KpiTile
          label="Merged, not churned"
          value={String(s?.merged_count ?? 0)}
          sub={`${fmtCur(s?.merged_mrr ?? 0)} MRR moved, not lost`}
          onClick={() => setTab(tab === "merged" ? "all" : "merged")}
          selected={tab === "merged"}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 border-b border-border">
          {(
            [
              ["all", `All (${accounts.length})`],
              ["missing_notice", `No notice (${s?.missing_notice_count ?? 0})`],
              ["confirmed", `Date on record (${s?.confirmed_count ?? 0})`],
              ["merged", `Merged (${s?.merged_count ?? 0})`],
              ["netted_to_zero", `Netted to $0 (${s?.netted_to_zero_count ?? 0})`],
              ["settled", `Settled (${settled.length})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium",
                tab === key
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {data?.locked && (
            <span className="inline-flex items-center gap-1 font-medium text-success">
              <Lock className="h-3.5 w-3.5" /> Signed off — frozen
            </span>
          )}
          {data?.pulled_at && (
            <span title={new Date(data.pulled_at).toLocaleString()}>
              Pulled {relativeAgo(data.pulled_at)}
              {data.pulled_by_name ? ` by ${data.pulled_by_name}` : ""}
            </span>
          )}
          {canAct && (
            <Button variant="outline" size="sm" onClick={doPull} disabled={pull.isPending}>
              <RefreshCw
                className={cn("mr-1.5 h-3.5 w-3.5", pull.isPending && "animate-spin")}
              />
              {pull.isPending ? "Pulling…" : "Re-pull"}
            </Button>
          )}
        </div>
      </div>

      {tab === "settled" ? (
        <SettledList
          rows={settled}
          canAct={canAct}
          onUndo={(qbId, name) =>
            settleChurn.mutate(
              { qbId },
              {
                onSuccess: () => toast.success(`${name ?? qbId} is open again`),
                onError: (e) =>
                  toast.error("Couldn't reverse it", {
                    description: (e as Error).message,
                  }),
              },
            )
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(a) => String(a.qb_id)}
          error={error ? String(error) : null}
          emptyMessage="Nothing stopped billing in this window."
          containerClassName="max-h-[calc(100vh-18rem)]"
        />
      )}

      <div className="rounded-md border border-warning/40 bg-warning-light/40 px-3 py-2 text-xs">
        <p className="font-medium text-warning">
          Why this step writes the dates instead of reading them
        </p>
        <p className="mt-1 text-muted-foreground">
          The billing contracts carry a cancel date on 10 of 556 rows and a stop
          date on 18; the Cinch company record hasn't had a cancel date entered
          since April 2025. HubSpot is the only maintained source and it covers
          roughly half. So the close reads all three, settles one answer, and
          keeps it here — that settled end date is what the cash forecast reads
          to stop the revenue. The worklist reaches back{" "}
          {data?.lookback_months ?? 3} months, so an account nobody dispositioned
          keeps reappearing instead of quietly falling off.
        </p>
      </div>

      <SettleDialog
        account={settleTarget}
        pending={settleChurn.isPending}
        onClose={() => setSettleTarget(null)}
        onConfirm={(payload) => {
          const a = settleTarget;
          if (!a) return;
          settleChurn.mutate(
            { qbId: a.qb_id, settle: payload },
            {
              onSuccess: () => {
                toast.success(
                  `${a.cinch_account_name ?? a.qb_customer_name ?? a.qb_id} settled`,
                );
                setSettleTarget(null);
              },
              onError: (e) =>
                toast.error("Couldn't settle", {
                  description: (e as Error).message,
                }),
            },
          );
        }}
      />

      <FlagDialog
        period={period}
        account={flagTarget}
        onClose={() => setFlagTarget(null)}
      />
    </div>
  );
}

// ── the three sources, side by side ─────────────────────────────────────────

/** Shows what each system believes, so disagreement is visible rather than
 *  silently resolved by precedence. */
function SourceCell({ account }: { account: ChurnAccount }) {
  const entries: Array<[string, string | null]> = [
    ["Billing", account.stop_billing_at ?? account.contract_canceled_at],
    ["HubSpot", account.hs_churn_date],
    ["Company", account.company_cancel_date],
  ];
  const known = entries.filter(([, v]) => v);
  if (known.length === 0) {
    return (
      <span className="text-xs italic text-warning">
        nothing on record anywhere
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      {known.map(([label, value]) => (
        <span key={label} className="text-xs">
          <span className="text-muted-foreground">{label}:</span>{" "}
          <span className="tabular-nums">{fmtDate(value)}</span>
        </span>
      ))}
      {account.notice_to_end_days != null && (
        <span
          className="text-[11px] text-muted-foreground"
          title="Days between notice and the end of billing — the lead time the cash forecast gets"
        >
          {account.notice_to_end_days}d notice
        </span>
      )}
    </div>
  );
}

// ── the settled list ────────────────────────────────────────────────────────

function SettledList({
  rows,
  canAct,
  onUndo,
}: {
  rows: SettledChurn[];
  canAct: boolean;
  onUndo: (qbId: number, name: string | null) => void;
}) {
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
        Nothing has been settled yet. Settling an account writes its churn dates
        here for good — and that's what the cash forecast reads.
      </p>
    );
  }
  return (
    <Card className="divide-y divide-border overflow-hidden p-0">
      {rows.map((r) => (
        <div key={r.qb_id} className="flex flex-wrap items-start gap-3 px-4 py-3">
          <div className="min-w-0 flex-1 basis-64">
            <div className="truncate text-sm font-semibold">
              {r.cinch_account_name ?? r.qb_customer_name ?? `QB ${r.qb_id}`}{" "}
              <Badge variant={r.outcome === "churned" ? "danger" : "neutral"}>
                {r.outcome === "churned"
                  ? "Churned"
                  : r.outcome === "merged"
                    ? "Merged"
                    : "Not churn"}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              {r.notice_date ? `Notice ${fmtDate(r.notice_date)} · ` : ""}
              {r.end_date ? `Ends ${fmtDate(r.end_date)} · ` : ""}
              {fmtCur2(r.mrr_at_churn)} MRR
              {r.date_source ? ` · from ${SOURCE_LABEL[r.date_source] ?? r.date_source}` : ""}
            </div>
            <div className="text-xs text-muted-foreground">
              Settled by {r.decided_by_name ?? "—"}
              {r.decided_in_period ? ` in the ${monthLabel(r.decided_in_period)} close` : ""}
            </div>
            {r.reason && <div className="mt-1 text-xs">“{r.reason}”</div>}
          </div>
          {canAct && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onUndo(r.qb_id, r.cinch_account_name ?? r.qb_customer_name)}
            >
              <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Reopen
            </Button>
          )}
        </div>
      ))}
    </Card>
  );
}

// ── dialogs ─────────────────────────────────────────────────────────────────

function SettleDialog({
  account,
  pending,
  onClose,
  onConfirm,
}: {
  account: ChurnAccount | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    outcome: ChurnOutcome;
    notice_date: string | null;
    end_date: string | null;
    reason: string | null;
  }) => void;
}) {
  const [outcome, setOutcome] = useState<ChurnOutcome>("churned");
  const [notice, setNotice] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const seen = useState<number | null>(null);

  // Reset the form when a different account opens the dialog, seeding from
  // whatever the close proposed (or from a prior settlement).
  if (account && seen[0] !== account.qb_id) {
    seen[1](account.qb_id);
    const prior = account.settled;
    setOutcome(prior?.outcome ?? (account.disposition === "merged" ? "merged" : "churned"));
    setNotice(prior?.notice_date ?? account.suggested_notice_date ?? "");
    setEnd(prior?.end_date ?? account.suggested_end_date ?? "");
    setReason(prior?.reason ?? account.hs_cancel_reason ?? "");
  }

  const name =
    account?.cinch_account_name ?? account?.qb_customer_name ?? String(account?.qb_id ?? "");
  const needsEnd = outcome === "churned" && !end;

  return (
    <Dialog open={account != null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Settle {name}</DialogTitle>
          <DialogDescription>
            This is the answer the whole company reads — later closes won't ask
            again, and the cash forecast uses the end date to stop the revenue.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {(
            [
              ["churned", "Churned"],
              ["merged", "Merged into another account"],
              ["not_churn", "Not churn"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setOutcome(value)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium",
                outcome === value
                  ? value === "churned"
                    ? "border-danger bg-danger/10 text-danger"
                    : "border-brand bg-brand/10 text-brand"
                  : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {account && (
          <div className="rounded-md border px-3 py-2 text-xs">
            <div className="text-muted-foreground">
              Last billed {monthLabel(account.last_billed_month?.slice(0, 7))} ·{" "}
              {fmtCur2(account.mrr_prior)} MRR
            </div>
            {(["billed_short", "sources_disagree"] as const)
              .filter((f) => account.flags.includes(f))
              .map((f) => (
                <div
                  key={f}
                  className="mt-1 flex items-start gap-1 font-medium text-warning"
                >
                  <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                  <span>{FLAG_LABEL[f]?.help}</span>
                </div>
              ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Notice date</span>
            <Input
              type="date"
              value={notice}
              onChange={(e) => setNotice(e.target.value)}
            />
            <span className="text-muted-foreground">
              When they told us
              {account?.suggested_notice_source
                ? ` · from ${SOURCE_LABEL[account.suggested_notice_source] ?? account.suggested_notice_source}`
                : ""}
            </span>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">
              End date {outcome === "churned" && <span className="text-danger">*</span>}
            </span>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
            <span className="text-muted-foreground">
              Last day we bill
              {account?.suggested_end_source
                ? ` · from ${SOURCE_LABEL[account.suggested_end_source] ?? account.suggested_end_source}`
                : ""}
            </span>
          </label>
        </div>

        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          aria-label="Reason"
          placeholder="Why did they leave? (kept with your name and the date)"
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onConfirm({
                outcome,
                notice_date: notice || null,
                end_date: end || null,
                reason: reason.trim() || null,
              })
            }
            disabled={needsEnd || pending}
            title={needsEnd ? "A churned account needs an end date" : undefined}
          >
            {pending ? "Saving…" : "Settle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlagDialog({
  period,
  account,
  onClose,
}: {
  period: string;
  account: ChurnAccount | null;
  onClose: () => void;
}) {
  const review = useReviewChurnAccount(period);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<ReviewStatus>("flagged");
  const seen = useState<number | null>(null);
  if (account && seen[0] !== account.qb_id) {
    seen[1](account.qb_id);
    setNotes(account.review_notes ?? "");
    setStatus("flagged");
  }
  const name =
    account?.cinch_account_name ?? account?.qb_customer_name ?? String(account?.qb_id ?? "");

  return (
    <Dialog open={account != null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Review {name}</DialogTitle>
          <DialogDescription>
            Flagged rows block completing this step. Note what CS needs to
            backfill — usually the notice date and reason.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["flagged", Flag, "Flag"],
              ["approved", Check, "Approve"],
              ["pending", RotateCcw, "Clear"],
            ] as const
          ).map(([value, Icon, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatus(value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium",
                status === value
                  ? value === "flagged"
                    ? "border-warning bg-warning-light text-warning"
                    : value === "approved"
                      ? "border-success bg-success-light text-success"
                      : "border-border bg-muted text-foreground"
                  : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          aria-label="Review notes"
          placeholder="What needs chasing? (e.g. no notice date in HubSpot — CS to confirm)"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!account) return;
              review.mutate(
                { qbId: account.qb_id, review: { status, notes } },
                {
                  onSuccess: () => {
                    toast.success(`${name} ${status === "pending" ? "cleared" : status}`);
                    onClose();
                  },
                  onError: (e) =>
                    toast.error("Couldn't save review", {
                      description: (e as Error).message,
                    }),
                },
              );
            }}
            disabled={review.isPending}
          >
            {review.isPending ? "Saving…" : "Save review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
